import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Shield, Cloud, Server, Cpu, Link as LinkIcon, Calendar } from 'lucide-react';

const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const HowItWorks = () => {
  const deploymentModels = [
    { 
      icon: Cloud, 
      title: "Cloud", 
      desc: "Fastest deployment. Streams secure low-res snippets to our cloud engine for analysis. Minimal setup required." 
    },
    { 
      icon: Server, 
      title: "On-Premise", 
      desc: "For high-security requirements. Processes all video locally within your network. No video leaves the site." 
    },
    { 
      icon: Cpu, 
      title: "Edge", 
      desc: "Hybrid model. Uses small edge-compute devices to process data locally and send only metadata to the dashboard." 
    }
  ];

  const securityFeatures = [
    { icon: Shield, title: "Compliance Ready", desc: "Built with privacy-first principles. Face blurring and metadata-only retention available." },
    { icon: LinkIcon, title: "Hardware Agnostic", desc: "Integrates with 95% of existing CCTV/NVR brands via ONVIF and RTSP protocols." },
    { icon: Server, title: "Data Residency", desc: "Complete control over where your data lives and how long it is stored." }
  ];

  const timeline = [
    { week: "Week 1", title: "Connectivity", desc: "Secure connection to NVRs. Camera calibration and zone definition." },
    { week: "Week 2", title: "Baselines", desc: "Initial data collection to establish operational benchmarks and staff patterns." },
    { week: "Week 3", title: "Insights", desc: "Full dashboard access. Alert systems live. First weekly optimization report." }
  ];

  return (
    <div data-testid="how-it-works-page" className="bg-white">
      {/* Hero */}
      <section className="py-16 lg:py-24 border-b border-border">
        <div className="container-custom">
          <AnimatedSection>
            <div className="max-w-3xl">
              <h1 className="text-4xl lg:text-6xl font-extrabold font-heading text-foreground mb-6">
                Low Friction. <span className="text-gradient">High Security.</span>
              </h1>
              <p className="text-xl text-muted-foreground">
                ProAct AI is designed to layer on top of your existing infrastructure, providing intelligence without the heavy lifting.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Deployment Models */}
      <section className="section-padding">
        <div className="container-custom">
          <AnimatedSection>
            <h2 className="text-3xl font-bold font-heading text-foreground mb-12">Flexible Deployment Models</h2>
          </AnimatedSection>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {deploymentModels.map((model, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div 
                  data-testid={`deployment-card-${i}`}
                  className="card-base p-8 h-full hover:-translate-y-1"
                >
                  <model.icon className="w-10 h-10 text-primary mb-6" />
                  <h3 className="text-xl font-bold font-heading text-foreground mb-4">{model.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{model.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Security & Timeline */}
      <section className="section-padding bg-secondary/30">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <AnimatedSection>
              <div>
                <h2 className="text-3xl font-bold font-heading text-foreground mb-8">Security & Integrity</h2>
                <div className="space-y-6">
                  {securityFeatures.map((feature, i) => (
                    <div key={i} className="flex" data-testid={`security-feature-${i}`}>
                      <div className="flex-shrink-0 mt-1">
                        <feature.icon className="w-6 h-6 text-primary" />
                      </div>
                      <div className="ml-4">
                        <h4 className="font-bold text-foreground">{feature.title}</h4>
                        <p className="text-sm text-muted-foreground">{feature.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.2}>
              <div 
                data-testid="timeline-section"
                className="bg-foreground rounded-2xl p-8 text-white shadow-2xl"
              >
                <h3 className="text-xl font-bold font-heading mb-8 flex items-center">
                  <Calendar className="mr-3" /> Implementation Timeline
                </h3>
                <div className="space-y-8">
                  {timeline.map((item, i) => (
                    <div key={i} className="relative pl-8 border-l-2 border-white/20">
                      <div className="absolute top-0 -left-[9px] w-4 h-4 rounded-full bg-primary"></div>
                      <span className="text-xs text-primary font-bold uppercase tracking-wider">{item.week}</span>
                      <h4 className="font-bold mt-1">{item.title}</h4>
                      <p className="text-sm text-white/60 mt-1">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
